import {
  Visibility,
  VisibilityOff,
  LockResetOutlined,
  CheckCircleOutlined,
  ErrorOutline,
} from '@mui/icons-material';
import { Button, CardContainer } from 'finara-components';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { authService } from '@/services/apiClient';

interface PasswordValidation {
  minLength: boolean;
  maxLength: boolean;
  hasUppercase: boolean;
  hasNumber: boolean;
  hasSymbol: boolean;
}

export const ResetPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [tokens, setTokens] = useState<{ accessToken: string; refreshToken: string } | null>(null);
  const [tokenError, setTokenError] = useState(false);

  // Extract tokens from URL hash on mount
  useEffect(() => {
    const hash = window.location.hash.substring(1);
    const params = new URLSearchParams(hash);

    const accessToken = params.get('access_token');
    const refreshToken = params.get('refresh_token');
    const type = params.get('type');

    if (accessToken && refreshToken && type === 'recovery') {
      setTokens({ accessToken, refreshToken });
    } else {
      setTokenError(true);
    }
  }, []);

  // Password validation
  const validatePassword = (password: string): PasswordValidation => ({
    minLength: password.length >= 8,
    maxLength: password.length <= 16,
    hasUppercase: /[A-Z]/.test(password),
    hasNumber: /[0-9]/.test(password),
    hasSymbol: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password),
  });

  const passwordValidation = validatePassword(newPassword);
  const isPasswordValid = Object.values(passwordValidation).every(Boolean);
  const passwordsMatch = newPassword === confirmPassword && confirmPassword.length > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!tokens) {
      setError('Token de recuperación inválido');
      return;
    }

    if (!isPasswordValid) {
      setError('La contraseña no cumple con los requisitos');
      return;
    }

    if (!passwordsMatch) {
      setError('Las contraseñas no coinciden');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await authService.resetPassword(tokens.accessToken, tokens.refreshToken, newPassword);
      setSuccess(true);
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Error al restablecer la contraseña. Intenta nuevamente.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Token error state
  if (tokenError) {
    return (
      <div className="min-h-screen flex flex-col justify-center py-12">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <CardContainer className="py-8 px-6 shadow-lg">
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <ErrorOutline className="w-8 h-8 text-red-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Enlace Inválido</h2>
              <p className="text-sm text-gray-600 mb-6">
                El enlace de recuperación de contraseña es inválido o ha expirado. Por favor,
                solicita un nuevo enlace de recuperación.
              </p>
              <Button variant="black" size="large" className="w-full" onClick={() => { void navigate('/login'); }}>
                Volver al Inicio de Sesión
              </Button>
            </div>
          </CardContainer>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen flex flex-col justify-center py-12">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <CardContainer className="py-8 px-6 shadow-lg">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircleOutlined className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Contraseña Actualizada</h2>
              <p className="text-sm text-gray-600 mb-6">
                Tu contraseña ha sido restablecida exitosamente. Ya puedes iniciar sesión con tu
                nueva contraseña.
              </p>
              <Button variant="black" size="large" className="w-full" onClick={() => { void navigate('/login'); }}>
                Iniciar Sesión
              </Button>
            </div>
          </CardContainer>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col justify-center py-12">
      {/* Header */}
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <LockResetOutlined className="w-8 h-8 text-blue-600" />
        </div>
        <h2 className="text-center text-3xl font-bold text-gray-900 mb-2">Nueva Contraseña</h2>
        <p className="text-center text-sm text-gray-600">Ingresa tu nueva contraseña</p>
      </div>

      {/* Reset Form */}
      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <CardContainer className="py-8 px-6 shadow-lg">
          <form className="space-y-6" onSubmit={e => void handleSubmit(e)}>
            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="text-sm text-red-700">{error}</div>
              </div>
            )}

            {/* New Password Field */}
            <div>
              <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-2">
                Nueva Contraseña
              </label>
              <div className="relative">
                <input
                  id="newPassword"
                  name="newPassword"
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <VisibilityOff className="h-5 w-5 text-gray-400" />
                  ) : (
                    <Visibility className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
            </div>

            {/* Password Requirements */}
            {newPassword.length > 0 && (
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <p className="text-xs font-medium text-gray-700 mb-2">Requisitos de contraseña:</p>
                <div className="grid grid-cols-1 gap-1">
                  <PasswordRequirement met={passwordValidation.minLength} text="Mínimo 8 caracteres" />
                  <PasswordRequirement met={passwordValidation.maxLength} text="Máximo 16 caracteres" />
                  <PasswordRequirement met={passwordValidation.hasUppercase} text="Al menos una mayúscula" />
                  <PasswordRequirement met={passwordValidation.hasNumber} text="Al menos un número" />
                  <PasswordRequirement met={passwordValidation.hasSymbol} text="Al menos un símbolo (!@#$%^&*...)" />
                </div>
              </div>
            )}

            {/* Confirm Password Field */}
            <div>
              <label
                htmlFor="confirmPassword"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Confirmar Contraseña
              </label>
              <div className="relative">
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  required
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <VisibilityOff className="h-5 w-5 text-gray-400" />
                  ) : (
                    <Visibility className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
              {confirmPassword.length > 0 && !passwordsMatch && (
                <p className="mt-1 text-xs text-red-600">Las contraseñas no coinciden</p>
              )}
              {passwordsMatch && (
                <p className="mt-1 text-xs text-green-600">Las contraseñas coinciden</p>
              )}
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              variant="black"
              size="large"
              disabled={isLoading || !isPasswordValid || !passwordsMatch}
              startIcon={isLoading ? undefined : <LockResetOutlined fontSize="inherit" />}
              className="w-full"
            >
              {isLoading ? 'Actualizando...' : 'Restablecer Contraseña'}
            </Button>
          </form>

          {/* Back to Login */}
          <div className="mt-6 text-center">
            <button
              type="button"
              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              onClick={() => { void navigate('/login'); }}
            >
              Volver al inicio de sesión
            </button>
          </div>
        </CardContainer>
      </div>
    </div>
  );
};

// Password requirement component
const PasswordRequirement: React.FC<{ met: boolean; text: string }> = ({ met, text }) => (
  <div className="flex items-center gap-2 text-xs">
    <span className={met ? 'text-green-500' : 'text-gray-400'}>
      {met ? '✓' : '○'}
    </span>
    <span className={met ? 'text-green-700' : 'text-gray-500'}>{text}</span>
  </div>
);
